// ═══════════════════════════════════════
// EDITOR/MAIN — Point d'entrée script.min.js
// ═══════════════════════════════════════

import '../common/main.js';

import { setAuthChangeCallback } from '../common/auth.js';
import { refreshEditorAccess, updateOwnerUI } from '../navigation.js';
import { initTransposeButtons } from '../transpose.js';
import { initKeyPicker } from '../key-picker.js';
import { initMeterPicker } from '../meter-picker.js';
import { initTempoPicker } from '../tempo-picker.js';
import { initUnitNotePicker } from '../unit-note-picker.js';
import { initFullscreen } from '../fullscreen.js';
import { initActionMenus } from '../action-menu.js';
import { initVoiceMenu } from '../voice-menu.js';
import { trackEvent } from '../analytics.js';
import { currentSlug, setCurrentId, setCurrentSlug, setCurrentPartitionMeta } from '../state.js';
import { markDirty, initAutosave } from '../autosave.js';
import { initCursorSync } from '../cursor-sync.js';

setAuthChangeCallback(() => {
    refreshEditorAccess();
    updateOwnerUI();
});

import { highlightAll, saveCursor, restoreCursor, getAbcForRender, getAbcContent, applyEdit, insertText, insertMultilineText, deleteSelection } from '../editor.js';
import {
    historyReset, historySchedulePush, historyPushNow,
    historyUndo, historyRedo, historyIsBlocked
} from '../history.js';
import { startDrones, stopDrones } from '../drone.js';
import { isPlaying, render, visualObj, currentSequenceCallback, SOUND_FONT_URL } from '../renderer.js';
import { DRONE_FREQS_HP } from '../config.js';
import { toggleAttachPanel, onAttachChange, openScoreFile, openAudioFile } from '../attachments.js';
import {
    openNewPartition, openNewPartitionWithInstrument, openPartitionParams,
    updateHeaderLine, initAddVoiceButton,
    wasParamsApplied, resetParamsApplied,
    parseHeadersFromAbc, applyInstrumentToVoice,
    startImportTuneFlow
} from '../header-form.js';
import { openShareModal } from '../share.js';
import { initFromServerData, savePartition, deletePartition, toggleVisibility, showAccessDenied } from '../navigation.js';


import {
    acGetContext, acClose, acInsert,
    acSetActive, acCheckTrigger, acIsDropdownOpen, acActiveIdx,
    acCanOpenPalette, acOpenPalette, initSnarePalette
} from '../autocomplete.js';
import { openGroupAssign } from '../groups.js';
import { downloadPdf, printPartition, downloadAbc, downloadMp3 } from '../export.js';


document.addEventListener('DOMContentLoaded', () => {

    // ── Partage ───────────────────────────────────────────────────────────────
    document.getElementById('share-btn')
        ?.addEventListener('click', () => {
            openShareModal();
            trackEvent('Editeur', 'share', currentSlug ?? null);
        });

    // ── Pickers globaux + boutons ─────────────────────────────────────────────
    initKeyPicker();
    initMeterPicker();
    initTempoPicker();
    initUnitNotePicker();
    initAddVoiceButton();
    initTransposeButtons();
    initFullscreen();
    initActionMenus();
    initVoiceMenu();
    initAutosave();
    initSnarePalette();
    initCursorSync();

    // Champs hf-* cachés — mis à jour par parseHeadersFromAbc
    const HEADER_FIELD_MAP = { 'hf-title': 'T', 'hf-composer': 'C', 'hf-source': 'S' };
    Object.entries(HEADER_FIELD_MAP).forEach(([id, tag]) => {
        document.getElementById(id)?.addEventListener('input', function () {
            updateHeaderLine(tag, this.value.trim());
            highlightAll();
            render();
            markDirty();
        });
    });

    // ── Bouton Paramètres ─────────────────────────────────────────────────────
    document.getElementById('btn-params')
        ?.addEventListener('click', () => openPartitionParams());

    // ── Délégué sur #voices-container ────────────────────────────────────────
    const voicesContainer = document.getElementById('voices-container');    
    let _skipNextInput = false;


voicesContainer?.addEventListener('keydown', (e) => {
        const voiceEl = e.target.closest('.abc-voice[contenteditable]');
        if (!voiceEl) return;

        // ── Undo / Redo ───────────────────────────────────────────────────────
        if (e.ctrlKey || e.metaKey) {
            if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); historyUndo(); return; }
            if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); historyRedo(); return; }
            // ── Ctrl+Espace → palette caisse claire ──────────────────────────
            if (e.code === 'Space' && acCanOpenPalette()) { e.preventDefault(); acOpenPalette(); return; }
        }

        // ── Autocomplétion ────────────────────────────────────────────────────
        if (acIsDropdownOpen()) {
            if (e.key === 'ArrowDown')                { e.preventDefault(); acSetActive(acActiveIdx() + 1); return; }
            if (e.key === 'ArrowUp')                  { e.preventDefault(); acSetActive(acActiveIdx() - 1); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); acInsert(acActiveIdx()); return; }
            if (e.key === 'Escape')                   { acClose(); return; }
        }

        // ── Entrée → nouvelle ligne ───────────────────────────────────────────
        if (e.key === 'Enter') {
            e.preventDefault();
            applyEdit(voiceEl, ({ lines, lineIdx, col }) => {
                const before = lines[lineIdx].slice(0, col);
                const after  = lines[lineIdx].slice(col);
                const next   = [...lines];
                next.splice(lineIdx, 1, before, after);
                return { lines: next, lineIdx: lineIdx + 1, col: 0 };
            });
            historyPushNow(getAbcContent(), saveCursor(voiceEl));
            render();
            acCheckTrigger(acGetContext());
            markDirty();
            return;
        }

        // ── Backspace → supprimer sélection, ou caractère avant, ou fusionner ──
        if (e.key === 'Backspace') {
            e.preventDefault();
            applyEdit(voiceEl, (state) => {
                const { lines, lineIdx, col, collapsed } = state;
                if (!collapsed) return deleteSelection(state);
                const next = [...lines];
                if (col > 0) {
                    next[lineIdx] = next[lineIdx].slice(0, col - 1) + next[lineIdx].slice(col);
                    return { lines: next, lineIdx, col: col - 1 };
                } else if (lineIdx > 0) {
                    const prevLen = next[lineIdx - 1].length;
                    next[lineIdx - 1] = next[lineIdx - 1] + next[lineIdx];
                    next.splice(lineIdx, 1);
                    return { lines: next, lineIdx: lineIdx - 1, col: prevLen };
                }
                return { lines: next, lineIdx, col };
            });
            historySchedulePush(getAbcContent(), saveCursor(voiceEl));
            render();
            acCheckTrigger(acGetContext());
            markDirty();
            return;
        }

        // ── Delete → supprimer sélection, ou caractère après, ou fusionner ───
        if (e.key === 'Delete') {
            e.preventDefault();
            applyEdit(voiceEl, (state) => {
                const { lines, lineIdx, col, collapsed } = state;
                if (!collapsed) return deleteSelection(state);
                const next = [...lines];
                if (col < next[lineIdx].length) {
                    next[lineIdx] = next[lineIdx].slice(0, col) + next[lineIdx].slice(col + 1);
                    return { lines: next, lineIdx, col };
                } else if (lineIdx < next.length - 1) {
                    next[lineIdx] = next[lineIdx] + next[lineIdx + 1];
                    next.splice(lineIdx + 1, 1);
                    return { lines: next, lineIdx, col };
                }
                return { lines: next, lineIdx, col };
            });
            historySchedulePush(getAbcContent(), saveCursor(voiceEl));
            render();
            acCheckTrigger(acGetContext());
            markDirty();
            return;
        }

        // ── Touches de navigation — laisser le navigateur gérer ──────────────
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
            return;
        }

        // ── Tab → insérer une espace (ou gérer l'autocomplétion) ─────────────
        if (e.key === 'Tab') {
            e.preventDefault();
            insertText(voiceEl, ' ');
            historySchedulePush(getAbcContent(), saveCursor(voiceEl));
            render();
            markDirty();
            return;
        }

        // ── Toutes les autres touches imprimables ─────────────────────────────
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            insertText(voiceEl, e.key);
            historySchedulePush(getAbcContent(), saveCursor(voiceEl));
            render();
            acCheckTrigger(acGetContext());
            markDirty();
        }
    });

    // ── Coller ────────────────────────────────────────────────────────────────
    voicesContainer?.addEventListener('paste', (e) => {
        const voiceEl = e.target.closest('.abc-voice[contenteditable]');
        if (!voiceEl) return;
        e.preventDefault();

        const cd   = e.clipboardData || window.clipboardData;
        const text = cd ? cd.getData('text/plain') : '';
        if (!text) return;

        insertMultilineText(voiceEl, text);
        historyPushNow(getAbcContent(), saveCursor(voiceEl));
        render();
        acCheckTrigger(acGetContext());
        markDirty();
    });

    // ── Couper ────────────────────────────────────────────────────────────────
    voicesContainer?.addEventListener('cut', (e) => {
        const voiceEl = e.target.closest('.abc-voice[contenteditable]');
        if (!voiceEl) return;
        e.preventDefault();

        const sel  = window.getSelection();
        const text = sel ? sel.toString() : '';
        if (!text) return;

        const cd = e.clipboardData || window.clipboardData;
        if (cd) cd.setData('text/plain', text);

        applyEdit(voiceEl, (state) => state.collapsed ? state : deleteSelection(state));
        historyPushNow(getAbcContent(), saveCursor(voiceEl));
        render();
        acCheckTrigger(acGetContext());
        markDirty();
    });

    // ── Input — uniquement pour les cas non interceptés (IME, dictée…) ────────
    voicesContainer?.addEventListener('input', (e) => {
        const voiceEl = e.target.closest('.abc-voice[contenteditable]');
        if (!voiceEl) return;
        const offset = saveCursor(voiceEl);
        highlightAll();
        restoreCursor(voiceEl, offset);
        if (!historyIsBlocked()) historySchedulePush(getAbcContent(), saveCursor(voiceEl));
        render();
        acCheckTrigger(acGetContext());
        markDirty();
    });


    document.addEventListener('mousedown', (e) => {
        const dd = document.getElementById('ornament-dropdown');
        if (dd && !dd.contains(e.target) && !e.target.closest('.abc-voice')) acClose();
    });

    // ── Bouton nouvelle partition ─────────────────────────────────────────────
    document.querySelector('.btn-add')
        ?.addEventListener('click', () => openNewPartition());

    // ── Footer éditeur ────────────────────────────────────────────────────────
    document.querySelector('.btn-save')
        ?.addEventListener('click', () => savePartition());
    document.getElementById('btn-attach')
        ?.addEventListener('click', () => toggleAttachPanel());
    document.getElementById('btn-delete')
        ?.addEventListener('click', () => deletePartition());
    document.getElementById('chk-visibility')
        ?.addEventListener('change', () => toggleVisibility());
    document.getElementById('btn-groups')
        ?.addEventListener('click', () => openGroupAssign());

    // ── Fichiers annexes ──────────────────────────────────────────────────────
    document.getElementById('file-score')
        ?.addEventListener('change', () => onAttachChange());
    document.getElementById('file-audio')
        ?.addEventListener('change', () => onAttachChange());
    document.getElementById('score-btn')
        ?.addEventListener('click', () => openScoreFile());
    document.getElementById('audio-ext-btn')
        ?.addEventListener('click', () => openAudioFile());

    // ── Contrôles lecture ─────────────────────────────────────────────────────
    document.getElementById('drone-btn')
        ?.addEventListener('click', () => {
            const btn = document.getElementById('drone-btn');
            const nowActive = btn.classList.toggle('active');
            if (nowActive) { if (isPlaying()) startDrones(); }
            else stopDrones();
        });
    document.getElementById('print-btn')
        ?.addEventListener('click', () => {
            printPartition(getAbcForRender());
            trackEvent('Editeur', 'print', currentSlug ?? null);
        });
    document.getElementById('pdf-btn')
        ?.addEventListener('click', () => {
            downloadPdf(getAbcForRender());
            trackEvent('Editeur', 'pdf', currentSlug ?? null);
        });
    document.getElementById('abc-export-btn')
        ?.addEventListener('click', () => {
            downloadAbc(getAbcForRender());
            trackEvent('Editeur', 'export_abc', currentSlug ?? null);
        });
    document.getElementById('mp3-export-btn')
        ?.addEventListener('click', async (e) => {
            const btn = e.currentTarget;
            if (btn.disabled) return;
            btn.disabled = true;
            btn.classList.add('is-busy');
            try {
                const dronesOn = document.getElementById('drone-btn')?.classList.contains('active');
                await downloadMp3(visualObj, {
                    sequenceCallback: currentSequenceCallback,
                    soundFontUrl:     SOUND_FONT_URL,
                    droneFreqs:       dronesOn ? DRONE_FREQS_HP : null,
                });
                trackEvent('Editeur', 'export_mp3', currentSlug ?? null);
            } catch (err) {
                console.error('[export mp3]', err);
            } finally {
                btn.disabled = false;
                btn.classList.remove('is-busy');
            }
        });
    document.getElementById('info-btn')
        ?.addEventListener('click', () => {
            const panel = document.getElementById('partition-info-panel');
            if (panel) panel.hidden = !panel.hidden;
        });

    // ── Resizer ───────────────────────────────────────────────────────────────
    initResizer();

    // ── Chargement initial ────────────────────────────────────────────────────
    const params        = new URLSearchParams(location.search);
    const newInstrument = params.get('new');
    const isImport      = params.get('import') === '1';
    const importAbc     = isImport ? sessionStorage.getItem('scorou_import_abc') : null;

    if (importAbc) {
        sessionStorage.removeItem('scorou_import_abc');
        const importInstrument = sessionStorage.getItem('scorou_import_instrument');
        sessionStorage.removeItem('scorou_import_instrument');
        history.replaceState({}, '', location.pathname);
        setCurrentId(null);
        setCurrentSlug(null);
        setCurrentPartitionMeta({ is_owner: true, is_public: false });
        document.getElementById('partition-title-display').textContent = '';
        document.getElementById('save-status').textContent = '';
        parseHeadersFromAbc(importAbc);
        if (importInstrument) {
            applyInstrumentToVoice(0, importInstrument);
        }
        historyReset(importAbc);
        refreshEditorAccess();
        render();
        updateOwnerUI();
        // Extraire le titre depuis l'ABC et lancer le flux tune
        const _importTitle = importAbc.match(/^T:\s*(.+)$/m)?.[1]?.trim() ?? '';
        if (_importTitle) startImportTuneFlow(_importTitle);
    } else if (window.__ACCESS_DENIED__) {
        showAccessDenied();
    } else if (newInstrument) {
        openNewPartitionWithInstrument(newInstrument);
        _watchModalCloseOnNew();
    } else if (window.__PARTITION__) {
        initFromServerData();
    } else {
        openNewPartition();
        _watchModalCloseOnNew();
    }
});


// ── Redirection si modale fermée sans sauvegarder ────────────────────────────
function _watchModalCloseOnNew() {
    if (!location.pathname.endsWith('/new')) return;
    const overlay = document.getElementById('modal-overlay');
    if (!overlay) return;
    resetParamsApplied();
    const observer = new MutationObserver(() => {
        if (!overlay.classList.contains('active')) {
            observer.disconnect();
            if (!wasParamsApplied()) window.location.href = '/partitions';
        }
    });
    observer.observe(overlay, { attributes: true, attributeFilter: ['class'] });
}

// ── Resizer ───────────────────────────────────────────────────────────────────
function initResizer() {
    const resizer    = document.getElementById('editor-resizer');
    const panel      = document.getElementById('editor-panel');
    if (!resizer || !panel) return;

    const isMobile = () => window.innerWidth <= 768;

    // ── Mobile : toggle click uniquement ─────────────────────────────────────
    if (isMobile()) {
        const siteHeader  = document.querySelector('header.site-header');
        const panelHeader = panel.querySelector('.editor-panel-header');

        const closedHeight = () => panelHeader ? panelHeader.offsetHeight : 0;

        const openHeight = () => {
            const siteH    = siteHeader  ? siteHeader.offsetHeight  : 0;
            const resizerH = resizer.offsetHeight;
            return window.innerHeight - siteH - resizerH;
        };

        panel.style.height = closedHeight() + 'px';
        resizer.setAttribute('aria-expanded', 'false');

        resizer.addEventListener('click', () => {
            const isOpen = resizer.classList.contains('editor-open');
            if (isOpen) {
                panel.style.height = closedHeight() + 'px';
                resizer.setAttribute('aria-expanded', 'false');
                resizer.classList.remove('editor-open');
            } else {
                panel.style.height = openHeight() + 'px';
                resizer.setAttribute('aria-expanded', 'true');
                resizer.classList.add('editor-open');
            }
        });

        return;
    }

    // ── Desktop : drag horizontal ─────────────────────────────────────────────
    function onMove(e) {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const newW = clientX - panel.getBoundingClientRect().left;
        const maxW = window.innerWidth * 0.7;
        if (newW < 30) {
            panel.style.width    = '0px';
            panel.style.minWidth = '0px';
            panel.style.overflow = 'hidden';
        } else {
            panel.style.overflow = '';
            panel.style.width    = Math.min(maxW, Math.max(0, newW)) + 'px';
            panel.style.minWidth = '0';
        }
    }

    function onUp() {
        resizer.classList.remove('dragging');
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup',   onUp);
        document.body.style.userSelect = '';
        document.body.style.cursor     = '';
    }

    resizer.addEventListener('mousedown', (e) => {
        e.preventDefault();
        resizer.classList.add('dragging');
        document.body.style.userSelect = 'none';
        document.body.style.cursor     = 'col-resize';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup',   onUp);
    });

    resizer.addEventListener('dblclick', () => {
        const w = parseInt(panel.style.width) || panel.offsetWidth;
        panel.style.width    = w < 30 ? '340px' : '0px';
        panel.style.minWidth = w < 30 ? '' : '0';
        panel.style.overflow = w < 30 ? '' : 'hidden';
    });
}